<template>
	<div
		v-if="specialBuildingPack"
		class="special-building-pack-popup"
		:class="`special-building-pack-popup--${specialBuildingPack?.attributes.type}-pack`"
	>
		<div class="special-building-pack-popup__header">
			<AppIconButton
				class="special-building-pack-popup__header-visibility"
				:type="IconTypes.HIDE"
				@click="handleVisibilityChange"
				:iconSize="20"
			/>
			<div
				v-if="
					specialBuildingPack &&
					convertUnixTimeToRemainingSeconds(
						specialBuildingPack?.expired_at ?? 0,
					) > 0
				"
				class="special-building-pack-popup__header-timer"
			>
				<Timer
					:time="
						convertUnixTimeToRemainingSeconds(
							specialBuildingPack?.expired_at ?? 0,
						)
					"
					:iconSize="16"
					:fontSize="16"
					hasIcon
				/>
			</div>
			<div
				class="special-building-pack-popup__header-bg"
				:class="[
					`special-building-pack-popup__header-bg--${specialBuildingPack?.attributes.type}-pack`,
				]"
			>
				<div
					:class="[
						`${specialBuildingPack?.attributes.type}-pack-header-bg`,
						`building-offer-header-bg--generic_${specialBuildingPack?.attributes?.decorIndex}-410`,
					]"
				/>
			</div>

			<div
				:class="`${specialBuildingPack?.attributes.type}-pack-header-heading`"
			>
				<div class="heading-wrapper">
					<ShrinkText
						:text="
							translate(
								`offer.${specialBuildingPack?.store_id}${isSpecialBuildingPack ? '' : '.non_translatable'}`,
							)
						"
						:fontSize="20"
						:textClassList="`${specialBuildingPack?.attributes.type}-pack-header-ribbon-text`"
					/>
				</div>
				<div
					:class="[
						`${specialBuildingPack?.attributes.type}-pack-header-ribbon`,
						`building-offer-header-ribbon--generic_${specialBuildingPack?.attributes?.decorIndex}-410`,
					]"
				/>
			</div>
			<AppIconButton
				class="special-building-pack-popup__close-button"
				:type="IconTypes.CLOSE"
				@click="emit('closePopup')"
			/>
		</div>
		<div
			class="special-building-pack-popup__content"
			:class="`special-building-pack-popup__content--special-building-${specialBuildingPack?.attributes?.decorIndex}`"
		>
			<div
				class="special-building-pack-popup__reward-wrapper"
				:class="`special-building-pack-popup__reward-wrapper--${specialBuildingPack?.attributes.type}-pack`"
			>
				<span class="section-label">
					{{ translate("ui.bundle_includes") }}
				</span>

				<div class="premium-building-header">
					<div class="premium-building-icon">
						<Tooltip
							customMaxWidth="16rem"
							:placement="Placements.TOP"
						>
							<template #element>
								<div
									v-if="
										specialBuildingPack.attributes.rewards
									"
									:class="`icon-${getTileIconName(Object.keys(specialBuildingPack.attributes.rewards)[0] as FrameName)}-96`"
								/>
							</template>
							<template #tooltip>
								<span
									class="reward-tooltip"
									v-html="
										specialBuildingPack.attributes.rewards
											? `<strong>${Object.values(specialBuildingPack.attributes.rewards)[0]}x</strong> ${translate(Object.keys(specialBuildingPack.attributes.rewards)[0] + '.name')}`
											: ''
									"
								/>
							</template>
						</Tooltip>
					</div>
					<span
						class="premium-building-count"
						v-if="specialBuildingPack.attributes.rewards"
					>
						{{
							`${Object.values(specialBuildingPack.attributes.rewards)[0]}x`
						}}
					</span>
				</div>

				<div class="premium-building-description">
					<div class="wax-seal" :class="`bg-wax-seal--red-92`">
						<span class="percentage-discount">
							{{
								`${specialBuildingPack?.attributes.percentage_discount}% OFF`
							}}
						</span>
					</div>
					<div class="premium-building-description__content">
						<p
							v-if="buildingFrameName"
							v-html="
								switchParagraphFormat(
									buildingEntity?.description ?? '',
								)
							"
						/>
						<TileAggregationDetails
							:entity="buildingEntity as TileEntity"
							:isPromotion="true"
						/>
					</div>
					<RaggedBoxBackground
						slug="premium-building-detail"
						:skin="
							{
								stopColor1: '#F2DCB5',
								stopColor2: '#FFF3DD',
							} as Skin
						"
					/>
				</div>
			</div>
		</div>
		<div class="special-building-pack-popup-decoration">
			<GoldenCorner bottom left />
			<GoldenCorner bottom right />
		</div>

		<Teleport to="#popup-outer-bar-target" v-if="isMounted">
			<div class="special-building-pack-popup__action-bar">
				<div
					v-if="
						specialBuildingPack &&
						specialBuildingPack.attributes.originValue &&
						isSpecialBuildingPack
					"
					class="special-building-pack-popup__origin-price"
				>
					<span>{{ `${translate("ui.original_price")}:` }}</span>

					<AppPrice
						v-if="
							typeof specialBuildingPack.attributes
								.originValue === 'object'
						"
						:amount="
							Object.values(
								specialBuildingPack.attributes.originValue,
							)[0]
						"
						:currency="
							Object.keys(
								specialBuildingPack.attributes.originValue,
							)[0] as ResourceType
						"
						:resourceCheck="false"
					/>
				</div>
				<AppTextButton
					v-if="specialBuildingPack && isSpecialBuildingPack"
					class="special-building-pack-popup__price"
					:type="StatusTypes.SECONDARY"
					:fontSize="36"
					text=""
					:fontWeight="FontWeights.BOLD"
					:price="{
						type: Object.keys(
							specialBuildingPack?.attributes.value ?? {},
						)[0] as ResourceType,
						value: Object.values(
							specialBuildingPack?.attributes.value ?? {},
						)[0],
					}"
					@click="buySpecialBuildingPack"
				/>
				<span
					v-if="specialBuildingPack?.attributes?.limit"
					class="special-building-pack-popup__limit"
				>
					{{
						`${translate("ui.limit")} ${specialBuildingPack?.num_of_purchases ?? 0}/${specialBuildingPack.attributes.limit}`
					}}
				</span>
			</div>
		</Teleport>
	</div>
</template>

<script lang="ts" setup>
import AppIconButton from "@/components/AppIconButton.vue";
import AppPrice from "@/components/AppPrice.vue";
import AppTextButton from "@/components/AppTextButton.vue";
import ShrinkText from "@/components/ShrinkText.vue";
import GoldenCorner from "@/components/SVG/GoldenCorner.vue";
import type { Skin } from "@/components/SVG/RaggedBoxBackground.vue";
import RaggedBoxBackground from "@/components/SVG/RaggedBoxBackground.vue";
import TileAggregationDetails from "@/components/TileAggregationDetails.vue";
import Timer from "@/components/Timer.vue";
import Tooltip from "@/components/Tooltip.vue";
import { SPECIAL_BUILDING_PACK_COOLDOWN } from "@/config/constants";
import type { FrameName } from "@/game/entities/config";
import { ResourceType } from "@/game/entities/config/resources";
import type TileEntity from "@/game/entities/TileEntity";
import SoundService from "@/game/SoundService";
import useGameTicker from "@/hooks/useGameTicker";
import useRewardClaimGuard from "@/hooks/useRewardClaimGuard";
import useSocketService, { EmitStores } from "@/hooks/useSocketService";
import useTileRepository from "@/hooks/useTileRepository";
import router from "@/router";
import useBuildingsStorageStore from "@/store/useBuildingsStorageStore";
import type { InGameShopItem } from "@/store/useOfferStore";
import useOfferStore from "@/store/useOfferStore";
import {
	PopupDictionary,
	PopupType,
	usePopupStore,
} from "@/store/usePopupStore";
import useResourcesStore from "@/store/useResourcesStore";
import useSettingsStore from "@/store/useSettingsStore";
import useShopStore from "@/store/useShopStore";
import {
	FontWeights,
	IconTypes,
	Placements,
	RouteNames,
	SkinTypes,
	StatusTypes,
} from "@/types/ui";
import { convertUnixTimeToRemainingSeconds } from "@/utils/format";
import {
	setWithExpiry,
	SPECIAL_BUILDING_PACK_COOLDOWN_TIME,
} from "@/utils/inGameTimerOperations";
import { getTileIconName } from "@/utils/tileIconName";
import { translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import { computed, markRaw, onMounted, ref, watch } from "vue";

const claimGuard = useRewardClaimGuard();

const emit = defineEmits(["closePopup"]);
const ticker = markRaw(useGameTicker());

const tilerepo = markRaw(useTileRepository());

const offerStore = useOfferStore();
const { specialBuildingPack } = storeToRefs(offerStore);

const popupStore = usePopupStore();

const resourcesStore = useResourcesStore();
const resources = storeToRefs(resourcesStore);
const { decrease } = resourcesStore;
const isMounted = ref<boolean>(false);

const soundService = markRaw(SoundService.getInstance());

const settingsStore = useSettingsStore();
const { showConfirmGemsMessagesShop } = storeToRefs(settingsStore);

const shopStore = useShopStore();
const { hiddenLeftBoxOffers } = storeToRefs(shopStore);

const buildingsStorageStore = useBuildingsStorageStore();
const socketService = markRaw(useSocketService());

const buildingFrameName = computed((): FrameName | null => {
	if (!specialBuildingPack.value?.attributes) return null;
	const rewards = specialBuildingPack.value.attributes?.rewards as
		| Record<string, number>
		| undefined;
	return (
		(Object.keys(rewards as Record<string, number>)[0] as FrameName) ?? null
	);
});

const buildingEntity = computed((): TileEntity | null => {
	if (!buildingFrameName.value) return null;

	const entity = tilerepo.createEntityByFrameName(buildingFrameName.value);
	if (!entity) return null;

	return entity;
});

const isSpecialBuildingPack = computed((): boolean => {
	if (!specialBuildingPack.value) return false;
	return [
		"lok_gold_mine_premium_pack",
		"lok_industrial_lumber_mill_pack",
		"lok_ox_stable_pack",
		"lok_universal_warehouse_pack",
		"lok_large_stone_quarry_pack",
		"lok_large_clay_pit_pack",
		"lok_advanced_iron_mine_pack",
		"lok_patisserie_pack",
		"lok_carpentry_district_pack",
		"lok_stonemasonry_district_pack",
		"lok_tax_office_pack",
		"lok_industrial_foundry_pack",
		"lok_brickyard_pack",
	].includes(specialBuildingPack.value.store_id);
});

const closeOfferBuildingPackPopup = (): void => {
	emit("closePopup");
};

watch(
	(): InGameShopItem | null => specialBuildingPack.value,
	(value: InGameShopItem | null): void => {
		if (!value) closeOfferBuildingPackPopup();
	},
);

const emitGenericClick = (): void => {
	soundService.playGenerickClick();
};

const resumeGame = (): void => {
	// Resume ticker after the purchase process, regardless of the purchase process status
	if (!ticker.isLocked) ticker.start();
};
const switchParagraphFormat = (text: string): string => {
	text = text.replace("{b}", "<strong>");
	text = text.replace("{/b}", "</strong>");
	return text;
};
const buySpecialBuildingPack = async (): Promise<void> => {
	emitGenericClick();

	// Stop the game ticker before the purchase process
	if (!ticker.isLocked) {
		ticker.stop();
	}

	if (specialBuildingPack.value?.attributes?.value?.gems) {
		if (
			specialBuildingPack.value?.attributes?.value?.gems >
			resources.gems.value
		) {
			closeOfferBuildingPackPopup();
			resumeGame();
			setTimeout((): void => {
				router.push({
					name: RouteNames.SHOP,
					params: { scrollTo: ResourceType.GEMS },
				});
			}, 100);
			return;
		}
		if (
			!showConfirmGemsMessagesShop.value &&
			specialBuildingPack.value.attributes.rewards
		) {
			handleBuySpecialBuildingPack();
			closeOfferBuildingPackPopup();
			return;
		}

		closeOfferBuildingPackPopup();

		popupStore.showPopup({
			id: PopupDictionary.BUY_SPECIAL_BUILDING_PACK,
			type: PopupType.INFO,
			heading: translate("ui.shop"),
			body: {
				ctaText: translate("ui.pack_confirm_message"),
				price: specialBuildingPack.value.attributes.value as Record<
					string,
					number
				>,
			},
			skin: SkinTypes.PREMIUM,
			showSkipMessageNotice: true,
			actions: [
				{
					label: translate("button.buy"),
					buttonType: StatusTypes.PRIMARY,
					callback: async (): Promise<void> => {
						if (!specialBuildingPack.value?.attributes.rewards) {
							return;
						}
						handleBuySpecialBuildingPack();
					},
				},
				{
					label: translate("button.cancel"),
					buttonType: StatusTypes.DEFAULT,
					callback: async (): Promise<void> => {
						resumeGame();
						popupStore.hidePopup();
					},
				},
			],
			closeCallback: async (): Promise<void> => {
				// NOTE: handling in case of payment initiation but not completion
				resumeGame();
			},
			maxHeight: 20,
			isCentered: true,
			isPaddingless: true,
		});
	}
};

const handleBuySpecialBuildingPack = (): Promise<void> => {
	const isConfirmation =
		popupStore.state.id === PopupDictionary.BUY_SPECIAL_BUILDING_PACK;
	const actions = popupStore.actions;
	return claimGuard.guard(
		async (): Promise<void> => {
			if (!specialBuildingPack.value) return;
			const rewards = specialBuildingPack.value.attributes.rewards;
			const buildingFrameName = Object.keys(
				rewards ?? {},
			)[0] as FrameName;

			buildingsStorageStore.increase(
				buildingFrameName,
				Object.values(
					specialBuildingPack.value.attributes.rewards ?? {},
				)[0] as number,
				"reward claim",
			);

			decrease(
				ResourceType.GEMS,
				Object.values(
					specialBuildingPack.value.attributes.value ?? {},
				)[0] ?? 0,
				`leftbox - premium building pack - ${specialBuildingPack.value.store_id}`,
			);

			offerStore.setLastBuyedSpecialBuildingFrameName(buildingFrameName);
			setWithExpiry(
				SPECIAL_BUILDING_PACK_COOLDOWN_TIME,
				SPECIAL_BUILDING_PACK_COOLDOWN,
			);

			offerStore.clearSpecialBuildingPack();
			await socketService.emitSaveRequest({
				stores: [EmitStores.RESOURCES, EmitStores.BUILDINGS_STORAGE],
			});
			resumeGame();

			popupStore.hidePopup();

			setTimeout((): void => {
				popupStore.showPopup({
					id: PopupDictionary.PURCHASE_SUCCESS,
					type: PopupType.INFO,
					heading: translate("ui.purchase_successful"),
					body: translate("ui.thank_you"),
					skin: SkinTypes.LIGHT,
					isCentered: true,
					height: 23,
					actions: [
						{
							label: translate("button.claim"),
							buttonType: StatusTypes.SUCCESS,
							callback: async (): Promise<void> => {
								popupStore.hidePopup();
							},
						},
					],
					rewards: rewards as Partial<Record<ResourceType, number>>,
				});
			}, 200);
		},
		(): boolean =>
			!isConfirmation ||
			(popupStore.isVisible && popupStore.actions === actions),
	);
};

const handleVisibilityChange = (): void => {
	if (!specialBuildingPack.value) return;

	const isHidden = hiddenLeftBoxOffers.value.includes(
		specialBuildingPack.value.store_id,
	);

	const updated = isHidden
		? hiddenLeftBoxOffers.value.filter(
				(id: string): boolean =>
					id !== specialBuildingPack.value?.store_id,
			)
		: [...hiddenLeftBoxOffers.value, specialBuildingPack.value.store_id];

	emit("closePopup"); // close popup first

	setTimeout((): void => {
		// then hide left box after 0.5 seconds
		shopStore.setHiddenLeftBoxOffers(updated);
	}, 500);
};

onMounted((): void => {
	// The teleportation mechanism is activated once the parent component is ready.
	isMounted.value = true;
});
</script>

<style lang="scss" scoped>
.special-building-pack-popup {
	position: relative;
	border-radius: 0.15rem;
	background: linear-gradient(
		0deg,
		rgba(216, 169, 114, 1) 0%,
		rgba(226, 181, 145, 1) 50%,
		rgba(175, 146, 109, 1) 100%
	);
	padding: 0.125rem;
	width: 100%;
	@include flex-col-start;

	&--special_building-pack {
		min-height: 33em;
	}

	&__action-bar {
		width: 100%;
		@include flex-col-center;
		z-index: 10;
	}

	&__price {
		gap: 0.5rem;
		width: 18.5rem;
		height: 3rem;

		.text-button--inner .text-button__text-container {
			padding-bottom: 0.5rem;
			width: unset;
		}

		.dual-price {
			opacity: 0.9;
			color: #f2dcb5;
			font-size: 1rem;
		}
	}

	&__limit {
		color: #fff;
		font-size: 0.75rem;
		text-align: center;
		text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
	}

	&__header {
		position: relative;
		z-index: 1;
		width: 100%;
		height: 11.625rem;
		&-visibility {
			position: absolute;
			top: -0.25rem;
			left: -0.25rem;
			z-index: 1;
			box-shadow: inset 0 0 0.5rem 0 rgba(0, 0, 0, 0.3);
			border-width: 0.075rem;
			border-style: solid;
			border-image-source: linear-gradient(to top, #bd9032, #f0d386);
			border-image-slice: 1;
			background-image: linear-gradient(to top, #181a1d, #303239);
			width: 1.75rem;
			height: 1.75rem;
		}

		&-timer {
			position: absolute;
			top: 1.75rem;
			left: 0;
			background-image: linear-gradient(
				to right,
				rgba(85, 0, 20, 1),
				rgba(85, 0, 20, 0.8),
				rgba(85, 0, 20, 0.7)
			);
			padding: 0 0.375rem;
			padding-right: 2.25rem;
			width: auto;
			height: 1.625rem;
			@include flex-col-center;
			align-items: flex-start;
			z-index: 1;
			mask-image: linear-gradient(
				to right,
				black 0%,
				black 20%,
				black 70%,
				transparent 98%
			);

			&::before,
			&::after {
				position: absolute;
				left: 0;
				z-index: 1;
				background: #cf0032;
				width: 100%;
				height: 0.063rem;
				content: "";
			}

			&::before {
				top: 0;
			}

			&::after {
				bottom: 0;
			}

			&--offset {
				top: 2rem;
			}

			:deep(.timer__time) {
				padding-bottom: 0.125rem;
				color: #f2dcb5;
				font-size: 1rem;
			}
		}

		&-bg {
			display: flex;
			position: absolute;
			top: 0;
			left: 0;
			flex-direction: column;
			justify-content: center;
			align-items: center;
			width: 100%;
			height: 100%;
			overflow: hidden;

			&--special_building-pack {
				overflow-x: clip;
				overflow-y: visible;

				.special_building-pack-header-bg {
					margin-top: -2.813rem;
				}
			}
		}

		.special_building-pack-header {
			&-character {
				position: absolute;
				bottom: 0;
				left: 50%;
				transform: translateX(-50%);
			}

			&-heading {
				position: absolute;
				bottom: -1.75rem;
				left: 50%;
				transform: translateX(-50%);

				.special_building-pack-header-ribbon {
					// dynamically sets the size for the parent
					position: relative;
					filter: drop-shadow(0 0.5rem 0.75rem rgba(0, 0, 0, 0.8));
				}

				.heading-wrapper {
					position: absolute;
					top: calc(50% - 0.313rem);
					left: 50%;
					transform: translate(-50%, -50%);
					z-index: 1;
					width: 90%;

					:deep(.text-container) {
						.special_building-pack-header-ribbon-text {
							color: #f4d278;
							font-weight: 800;
							text-align: center;
							text-shadow: 0.044rem 0.044rem 0.063rem
								rgba(0, 0, 0, 0.6);
							text-transform: uppercase;
						}
					}
				}
			}
		}
	}

	&__content {
		position: relative;
		flex: 1 1 0;
		z-index: 0;
		width: 100%;

		&::before {
			position: absolute;
			top: 0;
			left: 0;
			opacity: 0.6;
			z-index: 0;
			// NOTE: Color-burn blending is better for dark colors
			mix-blend-mode: color-burn;
			background: repeat center;
			@include background(
				url($path-images + "backgrounds/textures/marble-texture-bg.avif"),
				40%,
				center,
				repeat
			);
			width: 100%;
			height: 100%;
			content: "";
		}

		.price {
			position: relative;

			.section-label {
				@include flex-row-center;
				position: relative;
				mask-image: linear-gradient(
					to right,
					transparent 2%,
					black 20%,
					black 80%,
					transparent 98%
				);
				border-width: 0.063rem 0 0.063rem 0;
				border-style: solid;
				border-image-source: linear-gradient(
					to bottom,
					rgba(216, 169, 114, 1),
					rgba(226, 181, 145, 1)
				);
				border-image-slice: 1;
				width: 100%;
				height: 2.125rem;

				span {
					position: relative;
					z-index: 1;
					padding-bottom: 0.25rem;
					color: #f4d278;
					font-weight: 800;
					font-size: 1.25rem;
					line-height: 1.25rem;
					text-align: center;
					text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
					text-transform: uppercase;
				}

				&::before {
					position: absolute;
					top: 0;
					left: 0;
					@include background(
						url($path-textures + "silk-flag-texture-bg.avif"),
						cover,
						center center,
						no-repeat
					);
					z-index: 0;
					z-index: 1;
					mix-blend-mode: multiply;
					width: 100%;
					height: 100%;
					content: "";
				}

				&::after {
					position: absolute;
					top: 0;
					left: 0;
					width: 100%;
					height: 100%;
					content: "";
				}

				:deep(.price span) {
					padding-bottom: 0.125rem;
				}
			}
		}

		.sub-rewards {
			position: relative;
			@include flex-col-center;

			.plus {
				margin-top: 0.75rem;
				padding-bottom: 1rem;
				color: #f2dcb5;
				font-weight: 800;
				font-size: 3rem;
				line-height: 0.5rem;
				text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
			}

			.rewards-wrapper {
				@include flex-row-center;
				gap: 3.5rem;

				.reward-box {
					position: relative;
					@include flex-col-center;
					gap: 0.25rem;

					&__icon {
						@include background(
							url($path-images + "shop/backgrounds/reward-box.avif"),
							contain,
							center,
							no-repeat
						);
						width: 4.313rem;
						height: 4.313rem;
						@include flex-row-center;
						filter: drop-shadow(
							0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6)
						);

						.wax-seal--bonus {
							position: absolute;
							top: -1.25rem;
							right: -1.5rem;
							@include flex-col-center;

							span {
								transform: translateY(-0.063rem) rotate(-6deg);
								transform-origin: center;
								color: #fff;
								font-weight: 800;
								font-size: 0.563rem;
								text-align: center;
								text-shadow:
									-0.063rem -0.063rem 0 #000,
									0rem -0.063rem 0 #000,
									0.063rem -0.063rem 0 #000,
									-0.063rem 0rem 0 #000,
									0.063rem 0rem 0 #000,
									-0.063rem 0.063rem 0 #000,
									0rem 0.063rem 0 #000,
									0.063rem 0.063rem 0 #000;
								text-transform: uppercase;
							}
						}
					}

					&__value {
						// NOTE: position + bottom to prevent boxes from shifting differently from the ‘+’ sign
						position: absolute;
						bottom: -1.5rem;
						color: #fff3dd;
						font-weight: 800;
						font-size: 1.188rem;
						line-height: 1.188rem;
						text-align: center;
						text-shadow: 0.044rem 0.044rem 0 rgba(0, 0, 0, 0.6);
						white-space: nowrap;

						&--upper {
							position: absolute;
							top: -1.75rem;
							bottom: unset;
							color: #f4d278;
							font-weight: 800;
							font-size: 1rem;
						}

						&--highlighted {
							color: #f4d278 !important;
						}
					}

					&.bonus {
						.reward-box__value {
							color: #f4d278;
						}
					}
				}

				&--building-pack {
					margin-top: 0.625rem;
					margin-bottom: 2.5rem;
				}
			}
		}

		// content skins

		&--special-building-1 {
			background-image: linear-gradient(0deg, #354d07, #68852b);
		}
		&--special-building-2 {
			background-image: linear-gradient(0deg, #224d65, #43b0bd);
		}

		&--generic_1 {
			background-image: linear-gradient(0deg, #21455f, #6bb6d7);
		}

		&--generic_2 {
			background-image: linear-gradient(0deg, #354d07, #68852b);
		}

		.multi-building-grid {
			@include flex-row-center;
			position: relative;
			justify-content: space-between;
			margin-top: 5rem;
			width: 100%;
			height: 100%;

			&::before {
				position: absolute;
				top: 3rem;
				left: 50%;
				width: 250%;
				height: 250%;
				content: "";
				@include background(
					url($path-images + "shop/backgrounds/radial-glow-xl.avif"),
					contain,
					center,
					no-repeat
				);
				transform: translate(-50%, -50%);
				z-index: -1;
				animation: rotateInfinite 20s linear infinite;
				pointer-events: none;
			}

			&-wrapper {
				position: relative;
				width: 100%;

				.wax-seal {
					top: -1rem;
					right: -3rem;
				}
			}

			&__item {
				@include flex-col-end;
				position: relative;
				justify-content: center;

				&--top-center {
					z-index: 1;
					margin-top: -50px;
				}

				&-quantity {
					position: relative;
					bottom: -0.125rem;
					margin-top: 0.5rem;
					padding-bottom: 1rem;
					color: #f4d278;
					font-weight: 800;
					font-size: 3.75rem;
					line-height: 2rem;
					text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
				}
			}
		}
	}

	&__reward-wrapper {
		position: relative;
		margin: 0.25rem auto 0;
		@include flex-col-center;

		&--special_building-pack {
			padding-bottom: 1rem;
			width: calc(100% - 2.5rem);
			overflow: visible;

			.wax-seal {
				top: -4.75rem;
				right: -3rem;
				z-index: 1;

				span {
					padding-top: 0.375rem;
					font-size: 1rem;
					text-transform: unset;
					&.percentage-discount {
						font-size: 1.5rem;
						line-height: 1.5rem;
					}
				}
			}

			.section-label {
				position: relative;
				margin-top: 1.75rem;
				padding: 0 2rem;
				width: 100%;
			}
		}

		// universal for starter and building offers
		.section-label {
			color: #fff;
			font-weight: 800;
			font-size: 0.875rem;
			text-align: center;
			text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
		}

		.pack-shine-wrapper {
			position: absolute;
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;

			.pack-shine {
				position: absolute;
				top: 0;
				left: 0;
				opacity: 0;
				z-index: 1;
				animation-duration: 1s;
				animation-timing-function: linear;
				animation-iteration-count: infinite;
				animation-name: twinkle;
				@include background(
					url($path-images + "shop/backgrounds/twinkle-spark.avif"),
					cover,
					center,
					no-repeat
				);
				width: 2.75rem;
				height: 2.75rem;

				&:nth-of-type(1) {
					top: 5rem;
					left: 6.5rem;
					animation-delay: 1950ms;
				}
				&:nth-of-type(2) {
					top: 4.5rem;
					left: 12rem;
					animation-delay: 1650ms;
				}
				&:nth-of-type(3) {
					top: 8rem;
					left: 8rem;
					animation-delay: 1350ms;
				}
			}
		}
	}

	&-decoration {
		position: absolute;
		top: 0;
		left: 0;
		z-index: 1;
		width: 100%;
		height: 100%;
		pointer-events: none;

		:deep(.golden-corner) {
			width: 1.5rem;
			height: 1.5rem;
		}

		:deep(.golden-corner--bottom-left) {
			position: absolute;
			bottom: 0;
			left: 0;
		}

		:deep(.golden-corner--bottom-right) {
			position: absolute;
			right: 0;
			bottom: 0;
		}
	}

	&__close-button {
		position: absolute;
		top: -1rem;
		right: -1rem;
	}
}

.reward-tooltip {
	text-align: center;
}

.wax-seal {
	position: absolute;
	@include flex-col-center;
	filter: drop-shadow(0 0.188rem 0.75rem rgba(35, 18, 4, 0.6));
	padding: 1rem;
	text-align: center;

	span {
		transform: translate(-0.125rem, -0.25rem) rotate(-6deg);
		transform-origin: center;
		color: #fff;
		font-weight: 800;
		font-size: 1.125rem;
		line-height: 1.125rem;
		text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
		text-transform: uppercase;
	}
}

.premium-building {
	&-wrapper {
		width: 100%;
		height: 100%;
		@include flex-col-center;
	}

	&-header {
		@include flex-row-center;
		gap: 0.875rem;
		margin-top: 0.75rem;
	}

	&-icon {
		position: relative;

		&::before {
			position: absolute;
			top: 50%;
			left: 50%;
			width: 500%;
			height: 500%;
			content: "";
			@include background(
				url($path-images + "shop/backgrounds/radial-glow-xl.avif"),
				contain,
				center,
				no-repeat
			);
			transform: translate(-50%, -50%);
			z-index: -1;
			animation: rotateInfinite 20s linear infinite;
			pointer-events: none;

			@keyframes rotateInfinite {
				0% {
					transform: translate(-50%, -50%) rotate(0deg);
				}
				100% {
					transform: translate(-50%, -50%) rotate(360deg);
				}
			}
		}
	}

	&-count {
		color: #f4d278;
		font-weight: 800;
		font-size: 3.75rem;
		text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
		text-transform: uppercase;
	}

	&-name {
		color: #fff3dd;
		font-weight: 800;
		font-size: 1rem;
		text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
		text-transform: uppercase;
	}

	&-description,
	&-bonus {
		@include flex-row-center;
		position: relative;
		width: calc(100% - 0.5rem);

		:deep(.ragged-box-bg) {
			position: absolute;
			z-index: -1;
			filter: drop-shadow(0 0 0.1rem rgba(49, 37, 22, 0.6));
			width: 100%;
			height: 100%;
		}
	}

	&-description {
		margin-top: 1rem;

		// Black Friday
		&--special-sale {
			width: 100%;
			@include flex-row-center;
			position: relative;
			margin-top: 2rem;
			box-shadow: inset 0 0 1rem 0rem #ffb330;
			background: linear-gradient(
				0deg,
				rgba(62, 45, 45, 1) 0%,
				rgba(90, 84, 84, 1) 100%
			);

			&::before {
				position: absolute;
				top: 0;
				left: 0;
				opacity: 0.6;
				mix-blend-mode: color-burn;
				background: url($path-images + "backgrounds/textures/marble-texture-bg.avif")
					repeat center;
				background-size: 40%;
				width: 100%;
				height: 100%;
				content: "";
			}

			.rewards-label {
				position: absolute;
				top: -1rem;
				z-index: 1;
				width: auto;
				height: 2rem;
			}

			.premium-building-description__content {
				@include flex-col-center;
				position: relative;
				gap: 0;
				padding: 1.5rem 1rem 1rem;
				width: 100%;
				min-height: 3.813rem;

				:deep(strong) {
					color: #13cedf;
				}

				p,
				span,
				:deep(.tile-aggregation-details-wrapper) {
					color: #f2dcb5;

					.price__value {
						color: #f2dcb5 !important;
					}
				}
			}
		}

		&__content {
			position: relative;
			padding: 1rem;
			width: 100%;
			font-size: 0.75rem;
			text-align: center;

			:deep(.price span) {
				color: #372615;
				font-size: 0.75rem;
				text-shadow: none;
			}

			:deep(strong) {
				color: #661094;
			}

			:deep(.tile-aggregation-details-wrapper) {
				@include flex-col-center;

				.tile-aggregation-details {
					&[data-scope="population-consumption"] {
						order: 3;
					}

					&[data-scope="food-consumption"],
					&[data-scope="population-consumption"] {
						.tile-aggregation-details__label,
						.price__value {
							font-weight: 500 !important;
							font-size: 0.75rem !important;
						}
					}

					&[data-scope="fishing-boat-count"],
					&[data-scope="sea-exploration-range"],
					&[data-scope="population"],
					&[data-scope="exploration-speed"],
					&[data-scope="range"],
					&[data-scope="production"],
					&[data-scope="storage"] {
						.tile-aggregation-details__label,
						.tile-aggregation-details__value {
							height: 100%;
							font-weight: 800 !important;
							font-size: 1rem !important;

							.price__value {
								font-weight: 800 !important;
								font-size: 1rem !important;
							}
						}

						.tile-aggregation-details__value {
							align-items: baseline;
						}

						.tile-aggregation-details__value,
						.price__value {
							color: #138f36;
						}

						.tile-aggregation-details__value-unit,
						.tile-aggregation-details__value-comma {
							color: #372615;
							font-size: 0.75rem !important;
						}
					}
				}
			}
		}
	}

	&-bonus {
		margin-top: 0.375rem;
		margin-bottom: 1.125rem;
		height: 3.5rem;

		&__content {
			position: absolute;
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;
			@include flex-row-center;
			z-index: 1;

			:deep(.price span) {
				color: #372615;
				text-shadow: none;
			}
		}
	}

	&-plus {
		margin-top: 0.375rem;
		padding-bottom: 1rem;
		color: #f2dcb5;
		font-weight: 800;
		font-size: 3rem;
		// fix for unconsistent font
		line-height: 0.5rem;
		text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
	}

	&-limit {
		margin-bottom: 0.5rem;
		color: #f2dcb5;
		font-size: 0.75rem;
		line-height: 0.75rem;
		text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
	}
}

.v-popper__popper .tooltip .body {
	.premium-building-description__content {
		@include flex-col-center;
		position: relative;
		gap: 0;
		width: 100%;
		min-height: 3.813rem;
		text-align: center;

		p,
		span {
			color: #372615;
			font-size: 0.875rem;
		}

		:deep(.tile-aggregation-details-wrapper) {
			align-items: center;
			color: #372615;
			font-size: 0.875rem;

			p,
			span {
				color: #372615;
				font-size: 0.875rem;
				text-shadow: none;
			}
		}
	}
}
</style>
